import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '../../../context/ToastContext';
import {
  CalendarIcon,
  ClockIcon,
  UserIcon,
  TagIcon,
  PlusIcon,
  XMarkIcon,
  VideoCameraIcon,
  PhoneIcon,
  MapPinIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  serverTimestamp,
  updateDoc,
  doc,
} from 'firebase/firestore';
import { firestore } from '../../../database/firebase';
import { useAuth } from '../../../context/AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';

const CreateSession = ({ onSessionCreated, editingSession, onClose }) => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(true);
  const [loading, setLoading] = useState(false);

  // Handle closing modal via onClose prop
  const handleClose = () => {
    setShowModal(false);
    if (onClose) {
      onClose();
    }
  };
  const [newSession, setNewSession] = useState({
    clientId: '',
    clientName: '',
    clientEmail: '',
    date: '',
    time: '',
    duration: '60',
    type: 'strategy',
    location: 'online',
    meetingLink: '',
    notes: '',
    status: 'scheduled',
  });

  // Initialize form when editing
  useEffect(() => {
    if (editingSession) {
      // Normalize date to YYYY-MM-DD format for HTML date input
      let normalizedDate = '';
      if (editingSession.date) {
        if (typeof editingSession.date === 'string') {
          // If it's already a string, extract YYYY-MM-DD part
          normalizedDate = editingSession.date.split('T')[0];
        } else if (editingSession.date.toDate) {
          // Firestore Timestamp
          const dateObj = editingSession.date.toDate();
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          normalizedDate = `${year}-${month}-${day}`;
        } else if (editingSession.date instanceof Date) {
          // Date object
          const year = editingSession.date.getFullYear();
          const month = String(editingSession.date.getMonth() + 1).padStart(2, '0');
          const day = String(editingSession.date.getDate()).padStart(2, '0');
          normalizedDate = `${year}-${month}-${day}`;
        }
      }

      setNewSession({
        clientId: editingSession.clientId || '',
        clientName: editingSession.clientName || '',
        clientEmail: editingSession.clientEmail || '',
        date: normalizedDate,
        time: editingSession.time || '',
        duration: editingSession.duration || '60',
        type: editingSession.type || 'strategy',
        location: editingSession.location || 'online',
        meetingLink: editingSession.meetingLink || '',
        notes: editingSession.notes || '',
        status: editingSession.status || 'scheduled',
      });
    } else {
      // Reset form for new session
      setNewSession({
        clientId: '',
        clientName: '',
        clientEmail: '',
        date: '',
        time: '',
        duration: '60',
        type: 'strategy',
        location: 'online',
        meetingLink: '',
        notes: '',
        status: 'scheduled',
      });
    }
  }, [editingSession]);

  useEffect(() => {
    if (!currentUser?.email) return;

    // Get effective email (parent coach email if delegated user)
    const delegatedUserData = localStorage.getItem('delegatedUser');
    let effectiveEmail = currentUser.email;

    if (delegatedUserData) {
      try {
        const parsed = JSON.parse(delegatedUserData);
        effectiveEmail = parsed.coachEmail || currentUser.email;
      } catch (e) {
        // Invalid JSON, use current user email
      }
    }

    const clientsQuery = query(
      collection(firestore, 'clients'),
      where('assignedCoachEmail', '==', effectiveEmail)
    );

    const unsubscribe = onSnapshot(clientsQuery, snapshot => {
      setClients(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => unsubscribe();
  }, [currentUser?.email]);

  const handleCreateSession = async () => {
    if (!newSession.clientId || !newSession.date || !newSession.time) {
      toast.warning('Please fill in all required fields', 'Validation Error');
      return;
    }

    setLoading(true);
    try {
      // Get effective email (parent coach email if delegated user)
      const delegatedUserData = localStorage.getItem('delegatedUser');
      let effectiveEmail = currentUser.email;

      if (delegatedUserData) {
        try {
          const parsed = JSON.parse(delegatedUserData);
          effectiveEmail = parsed.coachEmail || currentUser.email;
        } catch (e) {
          // Invalid JSON, use current user email
        }
      }

      // Normalize date to YYYY-MM-DD format to ensure consistency
      let normalizedDate = newSession.date;
      if (normalizedDate) {
        // If it's already in YYYY-MM-DD format, use it as-is
        // Otherwise, try to normalize it
        if (normalizedDate.includes('T')) {
          normalizedDate = normalizedDate.split('T')[0];
        }
        // Ensure it's exactly YYYY-MM-DD format
        const dateMatch = normalizedDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (!dateMatch) {
          // Try to parse and reformat
          const dateObj = new Date(normalizedDate);
          if (!isNaN(dateObj.getTime())) {
            const year = dateObj.getFullYear();
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const day = String(dateObj.getDate()).padStart(2, '0');
            normalizedDate = `${year}-${month}-${day}`;
          }
        }
      }

      const sessionData = {
        clientId: newSession.clientId,
        clientName: newSession.clientName,
        clientEmail: newSession.clientEmail,
        date: normalizedDate, // Save as string in YYYY-MM-DD format
        time: newSession.time,
        duration: newSession.duration,
        type: newSession.type,
        location: newSession.location,
        meetingLink: newSession.meetingLink || '',
        notes: newSession.notes || '',
        status: newSession.status,
        coachEmail: effectiveEmail,
        coachName: currentUser.displayName || effectiveEmail,
        updatedAt: serverTimestamp(),
      };

      if (editingSession) {
        // Update existing session
        await updateDoc(doc(firestore, 'sessions', editingSession.id), {
          ...sessionData,
          updatedAt: serverTimestamp(),
        });
        toast.success('Session updated successfully!');
      } else {
        // Create new session
        sessionData.createdAt = serverTimestamp();
        const sessionRef = await addDoc(collection(firestore, 'sessions'), sessionData);

        // Send email notification using Firebase Function
        try {
          const functions = getFunctions();
          const sendSessionEmail = httpsCallable(functions, 'sendSessionEmail');

          await sendSessionEmail({
            sessionId: sessionRef.id,
            clientEmail: newSession.clientEmail,
            clientName: newSession.clientName,
            coachName: sessionData.coachName,
            coachEmail: sessionData.coachEmail,
            date: newSession.date,
            time: newSession.time,
            duration: newSession.duration,
            type: newSession.type,
            location: newSession.location,
            meetingLink: newSession.meetingLink || '',
            notes: newSession.notes || '',
          });
        } catch (emailError) {
          // Don't fail the session creation if email fails
        }
      }

      toast.success(
        editingSession ? 'Session updated successfully!' : 'Session created successfully!'
      );

      // Wait a moment to ensure Firestore write is complete
      await new Promise(resolve => setTimeout(resolve, 100));

      // Reset form
      setNewSession({
        clientId: '',
        clientName: '',
        clientEmail: '',
        date: '',
        time: '',
        duration: '60',
        type: 'strategy',
        location: 'online',
        meetingLink: '',
        notes: '',
        status: 'scheduled',
      });

      handleClose();
      if (onSessionCreated) {
        onSessionCreated();
      }
    } catch (error) {
      toast.error(
        editingSession ? 'Failed to update session' : 'Failed to create session',
        'Error'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setShowModal(true)}
        className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition-all hover:from-blue-700 hover:to-blue-800 hover:shadow-xl"
      >
        <PlusIcon className="h-5 w-5" />
        Create Session
      </motion.button>

      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={handleClose}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="sticky top-0 mb-6 flex items-center justify-between border-b bg-white pb-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                    <CalendarIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {editingSession ? 'Edit Session' : 'Create New Session'}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {editingSession
                        ? 'Update session details'
                        : 'Schedule a coaching session with your client'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="rounded-lg p-2 transition-colors hover:bg-gray-100"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Client <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={newSession.clientId}
                    onChange={e => {
                      const selectedClient = clients.find(c => c.id === e.target.value);
                      setNewSession({
                        ...newSession,
                        clientId: e.target.value,
                        clientName: selectedClient
                          ? `${selectedClient.firstname || ''} ${selectedClient.lastname || ''}`.trim() ||
                            selectedClient.email
                          : '',
                        clientEmail: selectedClient?.email || '',
                      });
                    }}
                    className="w-full rounded-lg border border-gray-300 bg-white p-3 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value="">Select a client</option>
                    {clients.map(client => {
                      const clientName =
                        `${client.firstname || ''} ${client.lastname || ''}`.trim() || client.email;
                      const organization = client.organization || client.businessName || '';
                      const displayText = organization
                        ? `${clientName} (${organization}) - ${client.email}`
                        : `${clientName} - ${client.email}`;

                      return (
                        <option key={client.id} value={client.id}>
                          {displayText}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-700">
                      Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={newSession.date}
                      onChange={e => setNewSession({ ...newSession, date: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full rounded-lg border border-gray-300 bg-white p-3 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-700">
                      Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      value={newSession.time}
                      onChange={e => setNewSession({ ...newSession, time: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 bg-white p-3 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-700">
                      Duration
                    </label>
                    <select
                      value={newSession.duration}
                      onChange={e => setNewSession({ ...newSession, duration: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 bg-white p-3 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    >
                      <option value="30">30 minutes</option>
                      <option value="60">1 hour</option>
                      <option value="90">1.5 hours</option>
                      <option value="120">2 hours</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-700">
                      Session Type
                    </label>
                    <select
                      value={newSession.type}
                      onChange={e => setNewSession({ ...newSession, type: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 bg-white p-3 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    >
                      <option value="strategy">Strategy</option>
                      <option value="pitch practice">Pitch Practice</option>
                      <option value="financial">Financial Planning</option>
                      <option value="marketing">Marketing</option>
                      <option value="mentoring">Mentoring</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">Location</label>
                  <div className="grid grid-cols-2 gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="button"
                      onClick={() => setNewSession({ ...newSession, location: 'online' })}
                      className={`flex items-center justify-center gap-2 rounded-lg border-2 p-3 transition-all ${
                        newSession.location === 'online'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <VideoCameraIcon className="h-5 w-5" />
                      <span className="text-sm font-medium">Online</span>
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="button"
                      onClick={() => setNewSession({ ...newSession, location: 'in-person' })}
                      className={`flex items-center justify-center gap-2 rounded-lg border-2 p-3 transition-all ${
                        newSession.location === 'in-person'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <MapPinIcon className="h-5 w-5" />
                      <span className="text-sm font-medium">In-Person</span>
                    </motion.button>
                  </div>
                </div>

                {newSession.location === 'online' && (
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-700">
                      Meeting Link (Optional)
                    </label>
                    <input
                      type="url"
                      value={newSession.meetingLink}
                      onChange={e => setNewSession({ ...newSession, meetingLink: e.target.value })}
                      placeholder="https://meet.google.com/..."
                      className="w-full rounded-lg border border-gray-300 bg-white p-3 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>
                )}

                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={newSession.notes}
                    onChange={e => setNewSession({ ...newSession, notes: e.target.value })}
                    rows={3}
                    placeholder="Add any notes or agenda items for this session..."
                    className="w-full resize-none rounded-lg border border-gray-300 bg-white p-3 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>

                <div className="sticky bottom-0 flex flex-col gap-3 border-t bg-white pb-2 pt-4 sm:flex-row">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleCreateSession}
                    disabled={loading}
                    className="flex-1 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-blue-700 hover:to-blue-800 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {loading
                      ? editingSession
                        ? 'Updating...'
                        : 'Creating...'
                      : editingSession
                        ? 'Update Session'
                        : 'Create Session'}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleClose}
                    className="flex-1 rounded-lg border-2 border-gray-300 bg-white px-6 py-3 text-sm font-semibold text-gray-700 transition-all hover:border-gray-400 hover:bg-gray-50"
                  >
                    Cancel
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CreateSession;
